/**
 * Slide-over showing the invoice instalments behind one Explorer cell.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, ExternalLink } from "lucide-react";
import { fmtGbp, fmtGbpShort } from "../staff/CommissionedPanels";
import { fmtPeriod, GP_SOURCE_LABELS } from "./explorerUtil";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExplorerDrill({ query, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    axios.get(`${API}/api/proposals/pipeline/cfo/explorer/detail`, {
      params: {
        dimension: query.dimension, key: query.key, period: query.period,
        grain: query.grain, scheduled: query.scheduled ? 1 : 0,
        basis: query.basis || "all",
      },
    })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || "Could not load the detail."); });
    return () => { live = false; };
  }, [query]);

  return (
    <div className="fixed inset-0 z-50" data-testid="explorer-drill">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} data-testid="explorer-drill-backdrop" />
      <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-4 border-b border-stone-200 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">{fmtPeriod(query.period)}</p>
            <h3 className="font-serif text-xl text-slate-900 leading-tight">{query.label}</h3>
            {data && (
              <p className="text-xs text-stone-500 mt-1">
                {data.count} invoice{data.count === 1 ? "" : "s"} · <span className="font-mono text-teal-700">{fmtGbp(data.total)}</span>
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="explorer-drill-close"
            className="p-1.5 rounded-lg text-stone-400 hover:text-slate-800 hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!data && !error && (
            <div className="flex items-center gap-2 text-stone-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && <p className="text-sm text-amber-800 py-4">{error}</p>}
          {data && (
            <ul className="divide-y divide-stone-100">
              {data.items.map((i, idx) => (
                <li key={`${i.fiid || idx}`} className="py-2.5 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {i.client} <span className="text-stone-400 font-normal">· {i.project_name}</span>
                    </p>
                    <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                      {i.date} · {i.status}{i.pm && i.pm !== "Unassigned" ? ` · PM ${i.pm}` : ""} · {i.team}
                      {i.fiid ? ` · ${i.fiid}` : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-mono tabular-nums ${i.value < 0 ? "text-rose-700" : "text-slate-800"}`}>{fmtGbpShort(i.value)}</p>
                    {i.gp_eff != null && (
                      <p className="text-[10px] font-mono text-stone-400">
                        GP {fmtGbpShort(i.gp_eff)}
                        {i.gp_source && i.gp_source !== "fgp" && (
                          <span className="text-amber-600"> · {GP_SOURCE_LABELS[i.gp_source] || "est"}</span>
                        )}
                      </p>
                    )}
                  </div>
                  {i.cognito_admin && (
                    <a href={i.cognito_admin} target="_blank" rel="noreferrer"
                      className="mt-0.5 text-stone-300 hover:text-[#1B4F8A] transition-colors shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
